// ============================================================
// Silk Route — Global JS
// Rich scroll-driven interactions: parallax, reveal, magnetic,
// horizontal carousels, char-reveal headings, countup, cursor
// ============================================================

(function () {
  'use strict';

  /* ─────────────────────────────────────────────
     Utility: lerp
  ───────────────────────────────────────────── */
  function lerp(a, b, t) { return a + (b - a) * t; }
  function clamp(v, min, max) { return Math.min(max, Math.max(min, v)); }

  /* Respect OS-level reduced-motion preference throughout this file */
  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ─────────────────────────────────────────────
     Reading progress bar
     Uses transform:scaleX (GPU-composited) instead
     of width, avoiding layout/paint on every scroll.
  ───────────────────────────────────────────── */
  var progressBar = document.getElementById('site-progress');
  if (progressBar) {
    window.addEventListener('scroll', function () {
      var s = document.documentElement.scrollTop;
      var h = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      progressBar.style.transform = 'scaleX(' + (h > 0 ? clamp(s / h, 0, 1) : 0) + ')';
    }, { passive: true });
  }

  /* ─────────────────────────────────────────────
     Scroll reveal — supports multiple variants
     .reveal | .reveal-up | .reveal-left | .reveal-right | .reveal-scale
     .reveal-delay-1 through .reveal-delay-6
  ───────────────────────────────────────────── */
  (function () {
    var els = document.querySelectorAll('.reveal, .reveal-up, .reveal-left, .reveal-right, .reveal-scale, [data-reveal]');
    if (!els.length) return;
    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.05, rootMargin: '0px 0px -12px 0px' });
    els.forEach(function (el) { obs.observe(el); });
  })();

  /* ─────────────────────────────────────────────
     Character-by-character heading reveal
     Add class .char-reveal to any heading.
     - Newlines in textarea content become <br> so
       intentional line-breaks are always preserved.
     - Each word is wrapped in white-space:nowrap so
       the browser ONLY breaks lines at spaces, never
       mid-word regardless of screen size.
     - Skipped entirely when prefers-reduced-motion
       is set; elements are made visible immediately.
  ───────────────────────────────────────────── */
  (function () {
    var charEls = document.querySelectorAll('.char-reveal');
    /* If user prefers reduced motion OR is on a touch/mobile device, reveal
       immediately — avoids creating hundreds of span nodes on phones which
       causes measurable long tasks and INP regression on mid-range devices. */
    var skipCharReveal = prefersReduced || window.matchMedia('(hover: none)').matches;
    if (skipCharReveal) {
      charEls.forEach(function (el) { el.style.opacity = '1'; });
      return;
    }
    charEls.forEach(function (el) {
      var text = el.textContent;
      el.textContent = '';
      el.setAttribute('aria-label', text);
      var charIdx = 0;

      function renderLine(lineText) {
        /* Split on spaces/tabs only (not newlines — handled above) */
        var words = lineText.trim().split(/[ \t]+/).filter(Boolean);
        words.forEach(function (word, wIdx) {
          if (wIdx > 0) {
            /* Regular space span — keeps soft-wrap opportunity between words */
            var spSp = document.createElement('span');
            spSp.textContent = ' ';
            spSp.style.cssText = 'opacity:0;transition:opacity 0.4s ease ' + (charIdx * 0.025) + 's';
            el.appendChild(spSp);
            charIdx++;
          }
          /* Word wrapper: inline-block + white-space:nowrap — no mid-word breaks */
          var wordEl = document.createElement('span');
          wordEl.style.cssText = 'display:inline-block;white-space:nowrap';
          Array.from(word).forEach(function (ch) {
            var span = document.createElement('span');
            span.textContent = ch;
            span.style.cssText =
              'display:inline-block;opacity:0;transform:translateY(20px);' +
              'transition:opacity 0.4s ease ' + (charIdx * 0.025) + 's,' +
              'transform 0.5s cubic-bezier(0.16,1,0.3,1) ' + (charIdx * 0.025) + 's';
            wordEl.appendChild(span);
            charIdx++;
          });
          el.appendChild(wordEl);
        });
      }

      /* Split source on newlines → each becomes a real <br> in the DOM */
      var lines = text.split(/\r?\n/);
      lines.forEach(function (line, lIdx) {
        if (lIdx > 0) el.appendChild(document.createElement('br'));
        if (line.trim()) renderLine(line);
      });

      var obs = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            el.querySelectorAll('span').forEach(function (s) {
              s.style.opacity = '1';
              s.style.transform = 'translateY(0)';
            });
            obs.unobserve(el);
          }
        });
      }, { threshold: 0.3 });
      obs.observe(el);
    });
  })();

  /* ─────────────────────────────────────────────
     Number count-up animation
     Add data-countup="2500" to any element
  ───────────────────────────────────────────── */
  (function () {
    function easeOutExpo(t) { return t === 1 ? 1 : 1 - Math.pow(2, -10 * t); }

    document.querySelectorAll('[data-countup]').forEach(function (el) {
      var target = parseFloat(el.dataset.countup) || 0;
      var suffix = el.dataset.suffix || '';
      var prefix = el.dataset.prefix || '';
      var duration = 1800;
      var started = false;

      var obs = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !started) {
            started = true;
            var start = performance.now();
            function tick(now) {
              var progress = clamp((now - start) / duration, 0, 1);
              var value = Math.round(easeOutExpo(progress) * target);
              el.textContent = prefix + value.toLocaleString('en-IN') + suffix;
              if (progress < 1) requestAnimationFrame(tick);
              else el.textContent = prefix + target.toLocaleString('en-IN') + suffix;
            }
            requestAnimationFrame(tick);
            obs.unobserve(el);
          }
        });
      }, { threshold: 0.5 });
      obs.observe(el);
    });
  })();

  /* ─────────────────────────────────────────────
     Parallax on [data-parallax] elements
     data-parallax-speed="0.4" (default 0.3)
     Skipped when prefers-reduced-motion is set,
     and on touch/mobile devices where parallax
     causes GPU overdraw and scroll jank.
  ───────────────────────────────────────────── */
  (function () {
    if (prefersReduced || window.matchMedia('(hover: none)').matches) return;
    var layers = Array.from(document.querySelectorAll('[data-parallax]'));
    if (!layers.length) return;
    var ticking = false;

    function updateParallax() {
      var scrollY = window.scrollY;
      layers.forEach(function (el) {
        var speed = parseFloat(el.dataset.parallaxSpeed || 0.3);
        var rect = el.parentElement.getBoundingClientRect();
        var centerY = rect.top + rect.height / 2;
        var offset = (centerY - window.innerHeight / 2) * speed;
        el.style.transform = 'translateY(' + offset.toFixed(2) + 'px)';
      });
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    }, { passive: true });
    updateParallax();
  })();

  /* ─────────────────────────────────────────────
     Horizontal drag-scroll carousels
     Add .drag-scroll to any scrollable container
  ───────────────────────────────────────────── */
  (function () {
    document.querySelectorAll('.drag-scroll').forEach(function (el) {
      var isDown = false;
      var startX = 0;
      var scrollLeft = 0;

      el.addEventListener('mousedown', function (e) {
        isDown = true;
        el.classList.add('is-dragging');
        startX = e.pageX - el.offsetLeft;
        scrollLeft = el.scrollLeft;
        e.preventDefault();
      });

      el.addEventListener('mouseleave', function () { isDown = false; el.classList.remove('is-dragging'); });
      el.addEventListener('mouseup', function () { isDown = false; el.classList.remove('is-dragging'); });
      el.addEventListener('mousemove', function (e) {
        if (!isDown) return;
        e.preventDefault();
        var x = e.pageX - el.offsetLeft;
        var walk = (x - startX) * 1.4;
        el.scrollLeft = scrollLeft - walk;
      });
    });
  })();

  /* ─────────────────────────────────────────────
     Magnetic buttons (Stripe-style)
  ───────────────────────────────────────────── */
  (function () {
    var BTN = 0.36, LBL = 0.16;
    document.querySelectorAll('.btn--primary, .btn--magnetic').forEach(function (btn) {
      var inner = btn.querySelector('.btn-inner') || btn;
      btn.addEventListener('mousemove', function (e) {
        var r = btn.getBoundingClientRect();
        var x = e.clientX - r.left - r.width / 2;
        var y = e.clientY - r.top - r.height / 2;
        btn.style.transform   = 'translate(' + (x * BTN) + 'px,' + (y * BTN) + 'px)';
        inner.style.transform = 'translate(' + (x * LBL) + 'px,' + (y * LBL) + 'px)';
      });
      btn.addEventListener('mouseleave', function () {
        btn.style.transform   = '';
        inner.style.transform = '';
      });
    });
  })();

  /* ─────────────────────────────────────────────
     Custom cursor (desktop only)
     Skipped when prefers-reduced-motion is set.
  ───────────────────────────────────────────── */
  (function () {
    if (prefersReduced || window.matchMedia('(hover: none)').matches) return;
    var cursor = document.createElement('div');
    cursor.id = 'sr-cursor';
    cursor.innerHTML = '<div id="sr-cursor-dot"></div>';
    document.body.appendChild(cursor);

    var cx = 0, cy = 0, tx = 0, ty = 0;
    var isHover = false;
    var raf;

    document.addEventListener('mousemove', function (e) { tx = e.clientX; ty = e.clientY; });

    document.querySelectorAll('a, button, [role="button"], .innovation-card, .queen-card, .ingredient-card').forEach(function (el) {
      el.addEventListener('mouseenter', function () {
        isHover = true;
        cursor.classList.add('is-hover');
      });
      el.addEventListener('mouseleave', function () {
        isHover = false;
        cursor.classList.remove('is-hover');
      });
    });

    function tick() {
      cx = lerp(cx, tx, 0.14);
      cy = lerp(cy, ty, 0.14);
      cursor.style.transform = 'translate(' + cx + 'px,' + cy + 'px)';
      raf = requestAnimationFrame(tick);
    }
    tick();

    /* Pause cursor RAF when tab is hidden — avoids burning CPU in background */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        cancelAnimationFrame(raf);
      } else {
        tick();
      }
    });
  })();

  /* ─────────────────────────────────────────────
     Scroll-driven background shift on dark sections
     Elements with .section-dark shift to parallax bg
  ───────────────────────────────────────────── */
  (function () {
    var sections = document.querySelectorAll('.section-dark[data-scroll-bg]');
    if (!sections.length) return;

    var bgTicking = false;
    window.addEventListener('scroll', function () {
      if (!bgTicking) {
        requestAnimationFrame(function () {
          sections.forEach(function (section) {
            var rect = section.getBoundingClientRect();
            var progress = clamp((-rect.top) / (rect.height - window.innerHeight), 0, 1);
            section.style.backgroundPosition = 'center ' + (progress * 40) + '%';
          });
          bgTicking = false;
        });
        bgTicking = true;
      }
    }, { passive: true });
  })();

  /* ─────────────────────────────────────────────
     Ingredient / Queens tab switcher
  ───────────────────────────────────────────── */
  (function () {
    document.querySelectorAll('[data-tab-group]').forEach(function (group) {
      var tabs = group.querySelectorAll('[data-tab]');
      var panels = group.querySelectorAll('[data-panel]');

      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          var target = tab.dataset.tab;
          tabs.forEach(function (t) { t.classList.remove('is-active'); t.setAttribute('aria-selected', 'false'); });
          panels.forEach(function (p) { p.hidden = p.dataset.panel !== target; });
          tab.classList.add('is-active');
          tab.setAttribute('aria-selected', 'true');
        });
      });
    });
  })();

  /* ─────────────────────────────────────────────
     Staggered card reveal for queen/ingredient grids
  ───────────────────────────────────────────── */
  (function () {
    var grids = document.querySelectorAll('.stagger-grid');
    grids.forEach(function (grid) {
      var cards = grid.querySelectorAll('.stagger-item');
      var obs = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            var idx = Array.from(cards).indexOf(entry.target);
            setTimeout(function () {
              entry.target.classList.add('is-visible');
            }, idx * 60); /* tighter stagger — 60ms instead of 90ms */
            obs.unobserve(entry.target);
          }
        });
      }, { threshold: 0.05, rootMargin: '0px 0px -8px 0px' });
      cards.forEach(function (c) { obs.observe(c); });
    });
  })();

  /* ─────────────────────────────────────────────
     Silk Route path SVG animation on scroll
     Finds #sr-path-line and animates stroke-dashoffset
  ───────────────────────────────────────────── */
  (function () {
    var path = document.getElementById('sr-path-line');
    if (!path) return;
    var length = path.getTotalLength ? path.getTotalLength() : 2000;
    path.style.strokeDasharray = length;
    path.style.strokeDashoffset = length;
    path.style.transition = 'stroke-dashoffset 0.05s linear';

    var section = path.closest('section') || path.closest('[data-path-section]');
    if (!section) return;

    window.addEventListener('scroll', function () {
      var rect = section.getBoundingClientRect();
      var progress = clamp((-rect.top) / (rect.height - window.innerHeight * 0.5), 0, 1);
      path.style.strokeDashoffset = length * (1 - progress);
    }, { passive: true });
  })();

  /* ─────────────────────────────────────────────
     Hovering tilt on product / queen cards
  ───────────────────────────────────────────── */
  (function () {
    document.querySelectorAll('.tilt-card').forEach(function (card) {
      card.addEventListener('mousemove', function (e) {
        var r = card.getBoundingClientRect();
        var x = (e.clientX - r.left) / r.width - 0.5;
        var y = (e.clientY - r.top) / r.height - 0.5;
        card.style.transform = 'perspective(700px) rotateX(' + (-y * 10) + 'deg) rotateY(' + (x * 10) + 'deg) translateZ(4px)';
      });
      card.addEventListener('mouseleave', function () {
        card.style.transform = 'perspective(700px) rotateX(0) rotateY(0) translateZ(0)';
      });
    });
  })();

  /* ─────────────────────────────────────────────
     Hero logo scroll indicator fade
  ───────────────────────────────────────────── */
  (function () {
    var scroll = document.querySelector('.hero-logo__scroll');
    if (!scroll) return;
    var heroTicking = false;
    window.addEventListener('scroll', function () {
      if (!heroTicking) {
        requestAnimationFrame(function () {
          scroll.style.opacity = Math.max(0, 1 - window.scrollY / 300);
          heroTicking = false;
        });
        heroTicking = true;
      }
    }, { passive: true });
  })();

  /* ─────────────────────────────────────────────
     Announcement bar pause on hover
  ───────────────────────────────────────────── */
  (function () {
    var bar = document.querySelector('.announcement-bar__ticker');
    if (!bar) return;
    bar.addEventListener('mouseenter', function () { bar.style.animationPlayState = 'paused'; });
    bar.addEventListener('mouseleave', function () { bar.style.animationPlayState = 'running'; });
  })();

  /* ─────────────────────────────────────────────
     Smooth scroll for internal anchor links
  ───────────────────────────────────────────── */
  document.querySelectorAll('a[href^="#"]').forEach(function (link) {
    link.addEventListener('click', function (e) {
      var id = link.getAttribute('href').slice(1);
      var target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });

})(); // end IIFE


/* ─────────────────────────────────────────────
   Scroll state — .is-scrolling on <body>
   While the page is in motion the body gets an
   .is-scrolling class. CSS uses this to set
   pointer-events:none on all descendants, which
   prevents :hover transforms from firing on
   elements that scroll under a stationary mouse
   cursor on desktop (the source of section-level
   flicker). Class is removed 150 ms after the
   last scroll event (covers trackpad momentum).
───────────────────────────────────────────── */
(function () {
  var t;
  window.addEventListener('scroll', function () {
    document.body.classList.add('is-scrolling');
    clearTimeout(t);
    t = setTimeout(function () {
      document.body.classList.remove('is-scrolling');
    }, 150);
  }, { passive: true });
})();


/* ─────────────────────────────────────────────
   Homepage Section Reels — DISABLED
   Removed: was blocking native scroll with
   e.preventDefault() + 900 ms lock, causing
   "trapped" scroll fatigue across all sections.
   Native browser scrolling is now unobstructed.
───────────────────────────────────────────── */


/* ─────────────────────────────────────────────
   Custom cursor CSS (injected to avoid render-
   blocking stylesheet for this decoration)
───────────────────────────────────────────── */
(function () {
  if (window.matchMedia('(hover: none)').matches) return;
  var style = document.createElement('style');
  style.textContent = [
    '#sr-cursor{position:fixed;top:0;left:0;pointer-events:none;z-index:99999;will-change:transform;}',
    '#sr-cursor-dot{width:12px;height:12px;border-radius:50%;background:#c4973c;margin-left:-6px;margin-top:-6px;transition:width 0.3s ease,height 0.3s ease,margin 0.3s ease,opacity 0.3s ease;}',
    '#sr-cursor.is-hover #sr-cursor-dot{width:36px;height:36px;margin-left:-18px;margin-top:-18px;opacity:0.6;}',
    '.is-dragging{cursor:grabbing!important;user-select:none;}'
  ].join('');
  document.head.appendChild(style);
})();
